import { useMemo } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Plus, Trash2, GripVertical, Lock } from "lucide-react";
import { RESULT_UNIT_TYPES } from "../lib/coachUtils";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Shared editor for the optional result-schema on a conditioning exercise.
 *
 * Props:
 *  - value:     Array<{ field_id, title, unit, order }> (schema fields)
 *  - onChange:  (nextList) => void
 *  - disabled:  boolean — set true when existing results lock the schema
 *  - disabledHint: string — message shown when disabled
 */
export default function ConditioningResultSchemaEditor({
	value,
	onChange,
	defaultUnit = "reps",
	disabled = false,
	disabledHint = null,
}) {
	const { t } = useLanguage();
	const fields = Array.isArray(value) ? value : [];
	const lockedHint = disabledHint || t("pbSchemaLockedHint");

	const makeFieldId = () =>
		`rsf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

	const addField = () => {
		if (disabled) return;
		const next = [...fields];
		next.push({
			field_id: makeFieldId(),
			title: "",
			unit: defaultUnit || "reps",
			order: next.length,
		});
		onChange?.(next);
	};

	const updateField = (idx, patch) => {
		if (disabled) {
			const patchKeys = Object.keys(patch || {});
			const titleOnlyEdit =
				patchKeys.length > 0 && patchKeys.every((k) => k === "title");
			if (!titleOnlyEdit) return;
		}
		const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
		onChange?.(next);
	};

	const removeField = (idx) => {
		if (disabled) return;
		const next = fields
			.filter((_, i) => i !== idx)
			.map((f, i) => ({ ...f, order: i }));
		onChange?.(next);
	};

	const moveField = (idx, dir) => {
		if (disabled) return;
		const target = idx + dir;
		if (target < 0 || target >= fields.length) return;
		const next = [...fields];
		[next[idx], next[target]] = [next[target], next[idx]];
		onChange?.(next.map((f, i) => ({ ...f, order: i })));
	};

	const isEmpty = useMemo(() => fields.length === 0, [fields]);

	return (
		<div className="space-y-2 border border-dashed border-border rounded-sm p-3 bg-secondary/30">
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbSchemaTitle")}
						</span>
						{disabled && <Lock className="w-3 h-3 text-muted-foreground" />}
					</div>
					<p className="text-[11px] text-muted-foreground mt-0.5">
						{disabled
							? lockedHint
							: t("pbSchemaConfiguredHint")}
					</p>
				</div>
				{!disabled && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="h-7 rounded-sm text-xs flex-shrink-0"
						onClick={addField}
					>
						<Plus className="w-3 h-3 mr-1" />
						{t("add")}
					</Button>
				)}
			</div>

			{isEmpty ? (
				<p className="text-[11px] text-muted-foreground italic">
					{t("pbSchemaEmpty")}
				</p>
			) : (
				<div className="space-y-1.5">
					{fields.map((f, idx) => (
						<div
							key={f.field_id || idx}
							className="flex items-center gap-1.5 bg-card border border-border rounded-sm p-1.5"
						>
							<div className="flex flex-col">
								<button
									type="button"
									disabled={disabled || idx === 0}
									onClick={() => moveField(idx, -1)}
									className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-0.5"
									aria-label={t("pbMoveUp")}
								>
									▲
								</button>
								<button
									type="button"
									disabled={disabled || idx === fields.length - 1}
									onClick={() => moveField(idx, 1)}
									className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-0.5"
									aria-label={t("pbMoveDown")}
								>
									▼
								</button>
							</div>
							<GripVertical className="w-3 h-3 text-muted-foreground flex-shrink-0" />
							<div className="flex-1 min-w-0 space-y-1">
								<Input
									value={f.title || ""}
									disabled={false}
									onChange={(e) => updateField(idx, { title: e.target.value })}
									placeholder={t("pbSchemaFieldTitlePlaceholder")}
									className="bg-secondary border-transparent rounded-sm text-xs h-7"
								/>
							</div>
							<div className="w-36 flex-shrink-0">
								<Select
									value={f.unit || defaultUnit || "reps"}
									disabled={disabled}
									onValueChange={(v) => updateField(idx, { unit: v })}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm text-xs h-7">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										{RESULT_UNIT_TYPES.map((u) => (
											<SelectItem
												key={u.value}
												value={u.value}
												className="text-xs"
											>
												{t(u.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								disabled={disabled}
								onClick={() => removeField(idx)}
								className="h-7 w-7 text-muted-foreground hover:text-blaze flex-shrink-0"
								aria-label={t("pbSchemaRemoveField")}
							>
								<Trash2 className="w-3 h-3" />
							</Button>
						</div>
					))}
				</div>
			)}

			<div className="flex items-center gap-1 pt-1">
				<Label className="text-[10px] text-muted-foreground">
					{t("pbSchemaOrderHint")}
				</Label>
			</div>
		</div>
	);
}
